import {
  FollowButtonOutline,
  FollowButtonSize,
  FollowButtonVariant,
  FollowType,
} from "@product/scmp-sdk";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { useUserFollow } from "~/components/follow-button/hooks";
import { MagazinesStyleVariant } from "~/components/section/section-style/const";
import type { TypeMagazinesStyleVariant } from "~/components/section/section-style/const";
import { useTrackImpressionEventByIntersection } from "~/components/tracking/google-analytics/hooks";
import type { Event } from "~/components/tracking/google-analytics/types";
import type { articleTopicFollowWidgetArticle$key } from "~/queries/__generated__/articleTopicFollowWidgetArticle.graphql";

import type { StyledProps } from "./styles";
import {
  Container,
  Divider,
  StyledEntityFollowButton,
  StyledMyNewsPromotionWidget,
  StyledTopicLink,
} from "./styles";

type Props = {
  className?: string;
  reference: articleTopicFollowWidgetArticle$key;
  variant?: "banner" | "stack" | TypeMagazinesStyleVariant;
} & Partial<PropsFromStyledProps<StyledProps>>;
export const ArticleTopicFollowWidget: FunctionComponent<Props> = ({
  className,
  reference: reference_,
  variant = "banner",
}) => {
  const { topics } = useFragment(
    graphql`
      fragment articleTopicFollowWidgetArticle on Article {
        topics {
          ...topicLinkTopic
          ...entityFollowButtonBase
          disableFollow
          entityId
        }
      }
    `,
    reference_,
  );

  const impressionEvent = useCallback<() => Event>(
    () => ({
      action: "Notification Center/Follow/Impression",
      category: "Notification Center",
      label: window.location.href,
    }),
    [],
  );
  const { captureTrackImpressionEventTargetElement } = useTrackImpressionEventByIntersection(
    variant === "banner" ? impressionEvent : null,
    { shouldSendOnce: true },
  );

  const { checkIsFollowed } = useUserFollow();

  if (!topics) return null;
  const mainTopic = first(topics);
  if (!mainTopic || mainTopic.disableFollow) return null;
  const isFollowed = checkIsFollowed(FollowType.Topic, mainTopic.entityId);

  return (
    <Container
      $isFollowed={isFollowed}
      $variant={variant}
      className={className}
      ref={captureTrackImpressionEventTargetElement}
    >
      <StyledTopicLink reference={mainTopic} />
      <Divider />
      <StyledEntityFollowButton
        isDisabledTooltip={variant === MagazinesStyleVariant ?? false}
        outline={variant === "banner" ? FollowButtonOutline.Plain : FollowButtonOutline.Outlined}
        reference={mainTopic}
        size={variant === "banner" ? FollowButtonSize.Compact : FollowButtonSize.Small}
        variant={FollowButtonVariant.Light}
        isTooltipFlipDisabled
      />
      {variant === "stack" && <StyledMyNewsPromotionWidget />}
    </Container>
  );
};

ArticleTopicFollowWidget.displayName = "ArticleTopicFollowWidget";
